import React, { useState, useMemo } from 'react';
import { Card, Typography, Dropdown } from '../../../../../components';
import classNames from 'classnames';
import {
    AppstoreOutlined,
    FolderOpenOutlined,
    PlusOutlined,
    DownOutlined,
    RightOutlined,
    MoreOutlined,
    HolderOutlined,
    StopOutlined,
} from '@ant-design/icons';
import './MoveGroupsAndAppsSidebar.scss';
import { useUserCustomization } from '../../../../../hooks/useUserCustomization';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '../../../../../components';
import { DragHandle, SortableItem, DroppableCard, DraggableNestedApp, DraggableAppCard } from './SidebarDnDComponents';
import { useSidebarDnD } from './useSidebarDnD';
import { useScrollMask } from '../../../../../components/ScrollMask';
import { formatCount } from '../../../helpers';

const { Title, Text } = Typography;

export const MoveGroupsAndAppsSidebar = ({
    moveGroups = [],
    applications = [],
    selectedGroup,
    setSelectedGroup,
    selectedApp,
    setSelectedApp,
    selectedInventory,
    setSelectedInventory,
    selectedUnassigned,
    setSelectedUnassigned,
    onCreateMoveGroup,
    onCreateApplication,
    onEditMoveGroup,
    onEditApplication,
    onDeleteMoveGroup,
    onDeleteApplication,
    onUnassignApplication,
    onChangeStatus,
    unsortedMachinesCount,
    unsortedMachinesLoading,
    unassignedMachinesCount,
    unassignedMachinesLoading,
    onDragAssignment,
    isDraggingVM = false,
    draggingVMRecord,
    onVMDragOver,
}) => {
    const [collapsedSections, setCollapsedSections] = useState({
        inventory: false,
        moveGroups: false,
        apps: false,
    });
    const [expandedGroup, setExpandedGroup] = useState(null);

    const { moveGroupsApplicationsOrder, updateMoveGroupsApplicationsOrder } = useUserCustomization();

    // Apply saved order from session storage whenever data changes
    const localMoveGroups = useMemo(() => {
        if (!moveGroups.length) return moveGroups;

        const savedOrder = moveGroupsApplicationsOrder?.move_groups;
        if (!savedOrder || savedOrder.length === 0) {
            return moveGroups;
        }

        const orderedGroups = [];
        const groupMap = new Map(moveGroups.map((g) => [g.id, g]));

        savedOrder.forEach((id) => {
            if (groupMap.has(id)) {
                orderedGroups.push(groupMap.get(id));
                groupMap.delete(id);
            }
        });

        groupMap.forEach((group) => orderedGroups.push(group));

        return orderedGroups;
    }, [moveGroups, moveGroupsApplicationsOrder]);

    // Apply saved order from session storage whenever data changes
    const localApps = useMemo(() => {
        if (!applications.length) return applications;

        const savedOrder = moveGroupsApplicationsOrder?.applications;
        if (!savedOrder || savedOrder.length === 0) {
            return applications;
        }

        const orderedApps = [];
        const appMap = new Map(applications.map((a) => [a.id, a]));

        savedOrder.forEach((id) => {
            if (appMap.has(id)) {
                orderedApps.push(appMap.get(id));
                appMap.delete(id);
            }
        });

        appMap.forEach((app) => orderedApps.push(app));

        return orderedApps;
    }, [applications, moveGroupsApplicationsOrder]);
    const [groupsScrollRef, groupsScrollClass] = useScrollMask([localMoveGroups, collapsedSections.moveGroups]);
    const [appsScrollRef, appsScrollClass] = useScrollMask([localApps, collapsedSections.apps]);

    const {
        dragOverId,
        activeDragItem,
        dragHoverGroupId,
        dragDisabledId,
        dragOverAddButton,
        sensors,
        handleDragStart,
        handleSidebarDragOver,
        handleUnifiedDragEnd,
    } = useSidebarDnD({
        localMoveGroups,
        localApps,
        selectedGroup,
        selectedApp,
        onDragAssignment,
        onVMDragOver,
        updateMoveGroupsApplicationsOrder,
        isDraggingVM,
        draggingVMRecord,
    });

    // Helper for InventoryCard-style label/value rendering
    const renderLabelValue = (text, className = '') => {
        const colonIdx = typeof text === 'string' ? text.indexOf(':') : -1;
        if (colonIdx !== -1) {
            const before = text.slice(0, colonIdx + 1);
            const after = text.slice(colonIdx + 1);
            return (
                <span className={classNames(className, 'with-padding-top-md')}>
                    <Text className="rm-card-muted-label">{before}</Text>
                    <Text className="semi-bold rm-card-muted-value">{after}</Text>
                </span>
            );
        } else {
            return <Text className={classNames('rm-card-muted', className)}>{text}</Text>;
        }
    };

    const toggleSection = (section) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const handleGroupClick = (groupId) => {
        setSelectedGroup(groupId);
        setExpandedGroup(groupId);
        setSelectedApp(null);
        setSelectedInventory(false);
        setSelectedUnassigned?.(false);
    };

    const handleAppClick = (appId) => {
        setSelectedApp(appId);
        setSelectedGroup(null);
        setSelectedInventory(false);
        setSelectedUnassigned?.(false);
    };

    const handleInventoryClick = () => {
        setSelectedInventory(true);
        setSelectedUnassigned?.(false);
        setSelectedGroup(null);
        setSelectedApp(null);
        setExpandedGroup(null);
    };

    const handleUnassignedClick = () => {
        setSelectedUnassigned?.(true);
        setSelectedInventory(false);
        setSelectedGroup(null);
        setSelectedApp(null);
        setExpandedGroup(null);
    };

    const getMoveGroupMenuItems = (group) => [
        {
            key: 'edit',
            label: 'Edit',
            onClick: (e) => {
                e.domEvent.stopPropagation();
                onEditMoveGroup?.(group);
            },
        },
        {
            key: 'delete',
            label: 'Delete',
            danger: true,
            onClick: (e) => {
                e.domEvent.stopPropagation();
                onDeleteMoveGroup?.(group.id);
            },
        },
    ];

    const getApplicationMenuItems = (app) => {
        const items = [];

        // Only show unassign option if app has a move group
        if (app.moveGroupId || (app.move_group_ids && app.move_group_ids.length > 0)) {
            items.push({
                key: 'unassign',
                label: 'Unassign',
                onClick: (e) => {
                    e.domEvent.stopPropagation();
                    onUnassignApplication?.(app);
                },
            });
        }

        items.push(
            {
                key: 'edit',
                label: 'Edit',
                onClick: (e) => {
                    e.domEvent.stopPropagation();
                    onEditApplication?.(app);
                },
            },
            {
                key: 'delete',
                label: 'Delete',
                danger: true,
                onClick: (e) => {
                    e.domEvent.stopPropagation();
                    onDeleteApplication?.(app.id);
                },
            },
        );

        return items;
    };

    return (
        <aside className="move-groups-apps-sidebar">
            <div className="sidebar-section">
                <div className="sidebar-section-header">
                    <Title level={2} className="sidebar-section-title">
                        Inventory
                    </Title>
                    <span className="sidebar-caret-icon" onClick={() => toggleSection('inventory')}>
                        {collapsedSections.inventory ? <RightOutlined /> : <DownOutlined />}
                    </span>
                </div>
                {!collapsedSections.inventory && (
                    <div className="sidebar-inventory-section-content">
                        <Card
                            className={classNames('sidebar-card', 'sidebar-inventory-card', {
                                'sidebar-card--selected': selectedInventory && !selectedUnassigned,
                            })}
                            onClick={handleInventoryClick}
                        >
                            <div className="sidebar-inventory-label">All</div>
                            <div className="sidebar-inventory-meta">
                                {renderLabelValue(
                                    `Machines: ${unsortedMachinesLoading ? '' : formatCount(unsortedMachinesCount)}`,
                                    'sidebar-inventory-machines',
                                )}
                            </div>
                        </Card>
                        <Card
                            className={classNames('sidebar-card', 'sidebar-inventory-card', {
                                'sidebar-card--selected': selectedUnassigned,
                            })}
                            onClick={handleUnassignedClick}
                        >
                            <div className="sidebar-inventory-label">Unassigned</div>
                            <div className="sidebar-inventory-meta">
                                {renderLabelValue(
                                    `Machines: ${unassignedMachinesLoading ? '' : formatCount(unassignedMachinesCount)}`,
                                    'sidebar-inventory-machines',
                                )}
                            </div>
                        </Card>
                    </div>
                )}
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleSidebarDragOver}
                onDragEnd={handleUnifiedDragEnd}
            >
                <div
                    className={classNames('sidebar-section', {
                        'sidebar-section--collapsed': collapsedSections.moveGroups,
                    })}
                >
                    <div className="sidebar-section-header">
                        <FolderOpenOutlined className="sidebar-section-icon" />
                        <Title level={2} className="sidebar-section-title">
                            Move Groups
                        </Title>
                        <span className="sidebar-caret-icon" onClick={() => toggleSection('moveGroups')}>
                            {collapsedSections.moveGroups ? <RightOutlined /> : <DownOutlined />}
                        </span>
                    </div>
                    {!collapsedSections.moveGroups && (
                        <>
                            <div
                                data-drag-add-btn="move-group"
                                className={classNames('sidebar-add-btn-wrapper', {
                                    'sidebar-add-btn-wrapper--drag-over': dragOverAddButton === 'move-group',
                                })}
                            >
                                <Button
                                    className="sidebar-add-btn"
                                    icon={<PlusOutlined />}
                                    size="small"
                                    type="dashed"
                                    block
                                    onClick={onCreateMoveGroup}
                                >
                                    Group
                                </Button>
                            </div>
                            <div
                                ref={groupsScrollRef}
                                className={classNames('sidebar-section-content', groupsScrollClass)}
                            >
                                <SortableContext
                                    items={localMoveGroups.map((g) => g.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {localMoveGroups.map((group) => (
                                        <SortableItem
                                            key={group.id}
                                            id={group.id}
                                            data={{ type: 'moveGroup', sourceName: group.name, reorderOnly: true }}
                                            groupZoneId={group.id}
                                        >
                                            <DroppableCard id={group.id} isOver={dragOverId === group.id}>
                                                {dragDisabledId === group.id && (
                                                    <StopOutlined className="sidebar-card-drag-disabled-badge" />
                                                )}
                                                <Card
                                                    className={classNames('sidebar-card', 'sidebar-move-group-card', {
                                                        'sidebar-card--selected': selectedGroup === group.id,
                                                        'sidebar-card--drag-over': dragOverId === group.id,
                                                        'sidebar-card--drag-disabled': dragDisabledId === group.id,
                                                    })}
                                                    onClick={() => handleGroupClick(group.id)}
                                                >
                                                    <div className="sidebar-card-row">
                                                        <DragHandle />
                                                        <div className="sidebar-card-main">
                                                            <Text className="sidebar-move-group-name">
                                                                {group.name}
                                                            </Text>
                                                            <div className="sidebar-move-group-meta">
                                                                {renderLabelValue(
                                                                    `Machines: ${formatCount(group.machines)}`,
                                                                    'sidebar-move-group-machines',
                                                                )}
                                                                {renderLabelValue(
                                                                    `Apps: ${group.apps}`,
                                                                    'sidebar-move-group-apps',
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="sidebar-card-actions">
                                                            <Dropdown
                                                                menu={{ items: getMoveGroupMenuItems(group) }}
                                                                trigger={['click']}
                                                                placement="bottomRight"
                                                            >
                                                                <MoreOutlined
                                                                    className="sidebar-card-ellipsis"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </Dropdown>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </DroppableCard>
                                            {(expandedGroup === group.id || dragHoverGroupId === group.id) &&
                                                group.applications &&
                                                group.applications.length > 0 && (
                                                    <div
                                                        className="sidebar-nested-apps"
                                                        data-nested-apps-group-id={String(group.id)}
                                                    >
                                                        {group.applications.map((app) => (
                                                            <DraggableNestedApp
                                                                key={app.id}
                                                                appId={app.id}
                                                                groupId={group.id}
                                                                sourceName={app.name}
                                                            >
                                                                <DroppableCard
                                                                    id={app.id}
                                                                    isOver={dragOverId === app.id}
                                                                >
                                                                    {dragDisabledId === app.id && (
                                                                        <StopOutlined className="sidebar-card-drag-disabled-badge" />
                                                                    )}
                                                                    <Card
                                                                        className={classNames(
                                                                            'sidebar-card',
                                                                            'sidebar-app-card',
                                                                            'sidebar-app-card--nested',
                                                                            {
                                                                                'sidebar-card--selected':
                                                                                    selectedApp === app.id,
                                                                                'sidebar-card--drag-over':
                                                                                    dragOverId === app.id,
                                                                                'sidebar-card--drag-disabled':
                                                                                    dragDisabledId === app.id,
                                                                            },
                                                                        )}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleAppClick(app.id);
                                                                        }}
                                                                    >
                                                                        <div className="sidebar-card-row">
                                                                            <div className="sidebar-card-main">
                                                                                <Text className="sidebar-app-name">
                                                                                    {app.name}
                                                                                </Text>
                                                                                <div className="sidebar-app-meta">
                                                                                    {renderLabelValue(
                                                                                        `Machines: ${formatCount(app.vms_count || 0)}`,
                                                                                        'sidebar-app-machines',
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <div className="sidebar-card-actions">
                                                                                <Dropdown
                                                                                    menu={{
                                                                                        items: getApplicationMenuItems(
                                                                                            app,
                                                                                        ),
                                                                                    }}
                                                                                    trigger={['click']}
                                                                                    placement="bottomRight"
                                                                                >
                                                                                    <MoreOutlined
                                                                                        className="sidebar-card-ellipsis"
                                                                                        onClick={(e) =>
                                                                                            e.stopPropagation()
                                                                                        }
                                                                                    />
                                                                                </Dropdown>
                                                                            </div>
                                                                        </div>
                                                                    </Card>
                                                                </DroppableCard>
                                                            </DraggableNestedApp>
                                                        ))}
                                                    </div>
                                                )}
                                        </SortableItem>
                                    ))}
                                </SortableContext>
                            </div>
                        </>
                    )}
                </div>

                <div
                    className={classNames('sidebar-section', {
                        'sidebar-section--collapsed': collapsedSections.apps,
                    })}
                >
                    <div className="sidebar-section-header">
                        <AppstoreOutlined className="sidebar-section-icon" />
                        <Title level={2} className="sidebar-section-title">
                            Applications
                        </Title>
                        <span className="sidebar-caret-icon" onClick={() => toggleSection('apps')}>
                            {collapsedSections.apps ? <RightOutlined /> : <DownOutlined />}
                        </span>
                    </div>
                    {!collapsedSections.apps && (
                        <>
                            <div
                                data-drag-add-btn="application"
                                className={classNames('sidebar-add-btn-wrapper', {
                                    'sidebar-add-btn-wrapper--drag-over': dragOverAddButton === 'application',
                                })}
                            >
                                <Button
                                    className="sidebar-add-btn"
                                    icon={<PlusOutlined />}
                                    size="small"
                                    type="dashed"
                                    block
                                    onClick={onCreateApplication}
                                >
                                    App
                                </Button>
                            </div>
                            <div ref={appsScrollRef} className={classNames('sidebar-section-content', appsScrollClass)}>
                                <SortableContext
                                    items={localApps.map((a) => a.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {localApps.map((app) => (
                                        <SortableItem
                                            key={app.id}
                                            id={app.id}
                                            data={{
                                                type: 'application',
                                                reorderOnly: true,
                                                sourceId: app.id,
                                                sourceName: app.name,
                                            }}
                                        >
                                            <DraggableAppCard appId={app.id} sourceName={app.name}>
                                                <DroppableCard id={app.id} isOver={dragOverId === app.id}>
                                                    {dragDisabledId === app.id && (
                                                        <StopOutlined className="sidebar-card-drag-disabled-badge" />
                                                    )}
                                                    <Card
                                                        className={classNames('sidebar-card', 'sidebar-app-card', {
                                                            'sidebar-card--selected': selectedApp === app.id,
                                                            'sidebar-card--drag-over': dragOverId === app.id,
                                                            'sidebar-card--drag-disabled': dragDisabledId === app.id,
                                                        })}
                                                        onClick={() => handleAppClick(app.id)}
                                                    >
                                                        <div className="sidebar-card-row">
                                                            <DragHandle />
                                                            <div className="sidebar-card-main">
                                                                <Text className="sidebar-app-name">{app.name}</Text>
                                                                <div className="sidebar-app-meta">
                                                                    {renderLabelValue(
                                                                        `Machines: ${formatCount(app.machines)}`,
                                                                        'sidebar-app-machines',
                                                                    )}
                                                                    {app.group && (
                                                                        <span className="sidebar-app-group">
                                                                            {' '}
                                                                            | {app.group}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="sidebar-card-actions">
                                                                <Dropdown
                                                                    menu={{ items: getApplicationMenuItems(app) }}
                                                                    trigger={['click']}
                                                                    placement="bottomRight"
                                                                >
                                                                    <MoreOutlined
                                                                        className="sidebar-card-ellipsis"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </Dropdown>
                                                            </div>
                                                        </div>
                                                    </Card>
                                                </DroppableCard>
                                            </DraggableAppCard>
                                        </SortableItem>
                                    ))}
                                </SortableContext>
                            </div>
                        </>
                    )}
                </div>

                <DragOverlay>
                    {activeDragItem?.data?.current?.type === 'moveGroup' &&
                        (() => {
                            const group = localMoveGroups.find((g) => g.id === activeDragItem.id);
                            if (!group) return null;
                            return (
                                <Card className="sidebar-card sidebar-move-group-card">
                                    <div className="sidebar-card-row">
                                        <span className="sidebar-drag-handle">
                                            <HolderOutlined />
                                        </span>
                                        <div className="sidebar-card-main">
                                            <Text className="sidebar-move-group-name">{group.name}</Text>
                                            <div className="sidebar-move-group-meta">
                                                {renderLabelValue(
                                                    `Machines: ${formatCount(group.machines)}`,
                                                    'sidebar-move-group-machines',
                                                )}
                                                {renderLabelValue(`Apps: ${group.apps}`, 'sidebar-move-group-apps')}
                                            </div>
                                        </div>
                                        <div className="sidebar-card-actions">
                                            <MoreOutlined className="sidebar-card-ellipsis" />
                                        </div>
                                    </div>
                                </Card>
                            );
                        })()}
                    {activeDragItem?.data?.current?.type === 'application' &&
                        (() => {
                            const activeId = activeDragItem.data.current.sourceId ?? activeDragItem.id;
                            const app =
                                localApps.find((a) => a.id === activeId) ||
                                localMoveGroups.flatMap((g) => g.applications || []).find((a) => a.id === activeId);
                            if (!app) return null;
                            return (
                                <Card className="sidebar-card sidebar-app-card">
                                    <div className="sidebar-card-row">
                                        <span className="sidebar-drag-handle">
                                            <HolderOutlined />
                                        </span>
                                        <div className="sidebar-card-main">
                                            <Text className="sidebar-app-name">{app.name}</Text>
                                            <div className="sidebar-app-meta">
                                                {renderLabelValue(
                                                    `Machines: ${formatCount(app.vms_count ?? app.machines ?? 0)}`,
                                                    'sidebar-app-machines',
                                                )}
                                                {app.group && <span className="sidebar-app-group"> | {app.group}</span>}
                                            </div>
                                        </div>
                                        <div className="sidebar-card-actions">
                                            <MoreOutlined className="sidebar-card-ellipsis" />
                                        </div>
                                    </div>
                                </Card>
                            );
                        })()}
                </DragOverlay>
            </DndContext>
        </aside>
    );
};
